import React, { useMemo } from 'react';
import { useLocation } from "react-router";
import { matchPath, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { WEB_CHAT_APP_URL } from '@/core/variables';
import { checkPermission, VIEW_PERMISSION_MAP } from "@/util/permissionUtil";
import { usePermissions } from '@/query/permission';
import { useScenarioId } from "@/hook";
import AppMenu from '@/component/AppMenu';
import AccountPopover from '@/component/AccountPopover';
import {
    BulkTestIcon, DashboardIcon, DeploymentIcon, MemberIcon,
    SettingIcon, ScenarioEditIcon, TemplateIcon, ConversationIcon, IntegrationIcon
} from "@/assets/svgs/icons";
import ArrowTopRightIcon from "@/assets/svgs/arrow_top_right.svg";

import ScenarioDropdown from "./ScenarioDropdown";


type MenuContent = {
    icon: React.FC<{className?: string}>,
    label: string,
    to: string,
    permissions?: readonly string[]
}

const ScenarioContextSideMenu: React.FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const scenarioId = useScenarioId();

    const { data: userPermissions } = usePermissions(scenarioId);

    const menuContents = useMemo<MenuContent[]>(() => [
        {
            icon: DashboardIcon,
            label: intl.formatMessage({ id: "m01" }),
            to: `/scenarios/${scenarioId}/dashboard`
        }, {
            icon: ScenarioEditIcon,
            label: intl.formatMessage({ id: "m02" }),
            to: `/scenarios/${scenarioId}/scenario-edit`,
            permissions: VIEW_PERMISSION_MAP.SCENE
        }, {
            icon: TemplateIcon,
            label: intl.formatMessage({ id: "m03" }),
            to: `/scenarios/${scenarioId}/template`,
            permissions: VIEW_PERMISSION_MAP.SCENE_TEMPLATE
        }, {
            icon: BulkTestIcon,
            label: intl.formatMessage({ id: "m04" }),
            to: `/scenarios/${scenarioId}/test`,
            permissions: VIEW_PERMISSION_MAP.BULK_TEST
        }, {
            icon: DeploymentIcon,
            label: intl.formatMessage({ id: "m05" }),
            to: `/scenarios/${scenarioId}/deploy`,
            permissions: VIEW_PERMISSION_MAP.DEPLOYMENT
        }, {
            icon: ConversationIcon,
            label: intl.formatMessage({ id: "m06" }),
            to: `/scenarios/${scenarioId}/conversation`,
            permissions: VIEW_PERMISSION_MAP.CONVERSATION_HISTORY
        }, {
            icon: IntegrationIcon,
            label: intl.formatMessage({ id: "m07" }),
            to: `/scenarios/${scenarioId}/integration`,
            permissions: VIEW_PERMISSION_MAP.INTEGRATION
        }, {
            icon: SettingIcon,
            label: intl.formatMessage({ id: "m08" }),
            to: `/scenarios/${scenarioId}/setting`,
            permissions: VIEW_PERMISSION_MAP.SETTING
        }, {
            icon: MemberIcon,
            label: intl.formatMessage({ id: "m09" }),
            to: `/scenarios/${scenarioId}/member`,
            permissions: VIEW_PERMISSION_MAP.MEMBER
        }
    ], [intl, scenarioId]);

    const filteredMenuContents = useMemo<MenuContent[]>(() => 
        menuContents.filter(menu => (
            !menu.permissions?.length || checkPermission(userPermissions ?? [], menu.permissions)
        ))
    , [menuContents, userPermissions]);

    if (!userPermissions) {
        return null;
    }
    return (
        <div className={classNames(
                 "w-[250px] h-full flex-none pt-[27px] pb-4.5 flex flex-col justify-between",
                 "bg-white border-r border-r-black-200 rounded-r-[20px] z-50"
             )}
        >
            <div className="flex gap-1 mb-5.5 mx-5 rounded-[6px]">
                <AppMenu className="text-chat42 hover:text-chat42-50 hover:bg-chat42-10" />
                <button className="w-full p-1 flex flex-col items-start gap-0.5"
                        onClick={() => navigate('/')}
                        onAuxClick={() => window.open('/', '_blank')}
                >
                    <div className="text-chat42 text-[14px] font-[600] leading-none group-hover:text-chat42-50">
                        Chat42
                    </div>
                    <div className="text-chat42 text-[17px] font-[700] leading-none group-hover:text-chat42-50">
                        Conversation Maker
                    </div>
                </button>
            </div>

             <div className="mb-2 px-5">
                <ScenarioDropdown scenarioId={scenarioId}/>
            </div>

             <div className="mb-4 px-5">
                <button className="w-full py-1.5 px-[15px] flex items-center justify-center gap-2 border border-black-200 rounded-[10px] hover:bg-black-100"
                        onClick={() => window.open(`${WEB_CHAT_APP_URL}/${scenarioId}`, '_blank')}
                >
                    <div className="text-[16px] font-[500] leading-none text-black-400">
                        {intl.formatMessage({ id: "b0024" })}
                    </div>
                    <img src={ArrowTopRightIcon} alt="webchat view" className="size-6" />
                </button>
            </div>

            <div className="h-full scroll-hover-container">
                <div className="h-full min-h-fit px-5">
                    <nav aria-label={intl.formatMessage({ id: "i04087" })}>
                        <div className="flex flex-col gap-3">
                            {filteredMenuContents.map(menu => (
                                <MenuFragment key={menu.label}
                                                menu={menu}
                                />
                            ))}
                        </div>
                    </nav>
                </div>
            </div>

            <div className="self-center">
                <AccountPopover anchor="right end"
                                className="ml-2"
                                scenarioId={scenarioId}
                />
            </div>
        </div>
    );
};

const MenuFragment: React.FC<{ menu: MenuContent }> = ({ menu }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isMatch = matchPath(`${menu.to}/*`, location.pathname);

    return (
        <button aria-current={isMatch ? "page" : undefined}
                className={classNames(
                    "p-2.5 flex gap-2.5 rounded-[6px]",
                    isMatch
                        ? "bg-chat42-10 text-chat42"
                        : "text-black-400"
                )}
                onClick={() => navigate(menu.to, { state: { from: "sidemenu" } })}
                onAuxClick={() => window.open(menu.to, "_blank")}
        >
            <menu.icon className="size-6" />
            <div className="font-[700] text-[16px] leading-[1.5]">
                {menu.label}
            </div>
        </button>
    );
};

export default ScenarioContextSideMenu;
